import React, { useEffect, useMemo, useCallback, useRef } from 'react';
import ReactDOMServer from 'react-dom/server';
import maplibregl from 'maplibre-gl';
import { clusterEvents } from '../../utils/mapUtils';
import { Event } from '../../types/event.types';
import { useGeolocation } from '../../stores/locationStore';
import {
  useMapReady,
  usePendingFlyTo,
  usePendingJumpTo,
  useSearchButtonVisible,
  useMapActions,
  MAP_CONFIG,
} from '../../stores/mapStore';
import { calculateDistance } from '../../utils/eventSorting';
import { Button, Icon, ContentBlock, MapMarker } from '../../pages/DesignSystem';
import { getRandomEmptyStateMessage } from './emptyStateMessages';
import { useAnalytics } from '../../contexts/AnalyticsContext';
import { getMapStyle, MapStyleKey } from './MAP_STYLES';
import { isWebGLAvailable } from './utils/webglSupport';
import MapFallback from './MapFallback';

import 'maplibre-gl/dist/maplibre-gl.css';
import './MapLibre.css';

// Style de carte à utiliser - change ici pour tester différents styles !
// ─────────────────────────────────────────────────────────────────────
// CARTO (gratuit illimité):
//   'CARTO_VOYAGER'        - 🎨 Coloré moderne (défaut)
//   'CARTO_POSITRON'       - ⚪ Minimaliste light
//   'CARTO_DARK'           - 🌙 Dark mode
// VERSATILES (gratuit, open source):
//   'VERSATILES_COLORFUL'  - 🌈 Très coloré et vivant
//   'VERSATILES_NEUTRINO'  - 🍦 Pastel doux
//   'VERSATILES_ECLIPSE'   - 🌑 Dark élégant
//   'VERSATILES_GRAYBEARD' - 🎩 Gris sophistiqué
// OPENFREEMAP (gratuit, communautaire):
//   'OPENFREEMAP_LIBERTY'  - 🗽 Liberty, très populaire
//   'OPENFREEMAP_BRIGHT'   - ☀️ Lumineux et clair
//   'OPENFREEMAP_POSITRON' - 🔘 Positron épuré
// AVEC CLÉ API:
//   'MAPTILER_STREETS', 'MAPTILER_DATAVIZ', 'STADIA_SMOOTH', 'STADIA_OUTDOORS'
// ─────────────────────────────────────────────────────────────────────
const CURRENT_MAP_STYLE: MapStyleKey = 'OPENFREEMAP_BRIGHT';

// ============================================================================
// TYPES
// ============================================================================

interface MapViewProps {
  events: Event[];
  onEventClick: (event: Event, clusterEvents?: Event[]) => void;
  onLocationChange?: (location: {
    latitude: number;
    longitude: number;
    shouldCenter?: boolean;
  }) => void;
  initialLocation?: { latitude: number; longitude: number };
  onMapClick?: () => void;
  isLoading?: boolean;
  /** Bascule vers la vue liste — utilisée par le fallback quand WebGL est indisponible. */
  onSwitchToList?: () => void;
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

const SearchAreaButton = React.memo(({
  onClick,
  isLoading,
}: {
  onClick: () => void;
  isLoading: boolean;
}) => (
  <div className="search-area-button-container">
    <Button
      onClick={onClick}
      disabled={isLoading}
      variant="outline"
    >
      <Icon name={isLoading ? "Loader" : "Search"} size={20} />
      <span>{isLoading ? "Loading..." : "Search in this area"}</span>
    </Button>
  </div>
));

const EmptyStateMessage = React.memo(() => {
  const message = useMemo(() => getRandomEmptyStateMessage(), []);

  return (
    <div className="map-empty-state">
      <ContentBlock
        title={message.title}
        subtitle={message.subtitle}
        variant="light"
        padding="md"
        size="sm"
        centered
      >
        <div />
      </ContentBlock>
    </div>
  );
});

const CompassButton = React.memo(({
  bearing,
  onClick,
}: {
  bearing: number;
  onClick: () => void;
}) => {
  // Ne pas afficher si la carte est orientée vers le nord (avec une tolérance de 1°)
  if (Math.abs(bearing) < 1) return null;

  const handlePointerDown = (e: React.PointerEvent) => {
    console.log('[Compass] 1️⃣ pointerdown reçu', {
      pointerType: e.pointerType,
      bearing,
      target: (e.target as HTMLElement)?.className,
    });
    e.stopPropagation();
    e.preventDefault();
    console.log('[Compass] 2️⃣ appel onClick()');
    onClick();
  };

  return (
    <button
      className="map-compass-button"
      onPointerDown={handlePointerDown}
      title="Reset north"
      aria-label="Réorienter la carte vers le nord"
    >
      <span style={{ transform: `rotate(${-bearing - 45}deg)`, display: 'flex' }}>
        <Icon name="Navigation" size={20} />
      </span>
    </button>
  );
});

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const MapLibreView: React.FC<MapViewProps> = React.memo(({
  events,
  onEventClick,
  onLocationChange,
  initialLocation,
  onMapClick,
  isLoading = false,
  onSwitchToList,
}) => {
  const analytics = useAnalytics();
  const { location } = useGeolocation();

  // Détection préventive WebGL : un seul appel getContext au montage (<5ms).
  // Si indisponible, on n'instancie jamais `new maplibregl.Map()` (voir Effet 1)
  // et on affiche directement MapFallback (pas d'erreur uncaught en console).
  const webglAvailable = useMemo(() => isWebGLAvailable(), []);

  // ========================================
  // REFS (Minimisées - seulement le nécessaire)
  // ========================================
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<maplibregl.Map | null>(null);
  const markers = useRef<maplibregl.Marker[]>([]);
  const skeletonMarkers = useRef<maplibregl.Marker[]>([]);
  const userMarker = useRef<maplibregl.Marker | null>(null);
  const markerHTMLCache = useRef<Map<string, string>>(new Map());

  // ========================================
  // MAP STORE - Source unique de vérité
  // ========================================
  const mapReady = useMapReady();
  const pendingFlyTo = usePendingFlyTo();
  const pendingJumpTo = usePendingJumpTo();
  const shouldShowSearchButton = useSearchButtonVisible();
  const {
    setReady,
    setCenter,
    setZoom,
    setSearchCenter,
    updateSearchButtonVisibility,
    clearPendingFlyTo,
    clearPendingJumpTo,
    setAnimating,
    flyTo,
  } = useMapActions();

  // État local pour le chargement de recherche uniquement
  const [isSearching, setIsSearching] = React.useState(false);
  const [visibleEvents, setVisibleEvents] = React.useState<number>(0);
  const [currentZoom, setCurrentZoom] = React.useState<number>(MAP_CONFIG.DEFAULT_ZOOM);
  const [bearing, setBearing] = React.useState<number>(0);

  // ========================================
  // COMPUTED VALUES
  // ========================================

  const finalLocation = useMemo(() => {
    if (initialLocation) {
      return [initialLocation.longitude, initialLocation.latitude] as [number, number];
    }
    if (location) {
      return [location.longitude, location.latitude] as [number, number];
    }
    return null;
  }, [initialLocation, location]);

  const shouldShowMap = finalLocation !== null;

  const clusters = useMemo(() => {
    return clusterEvents(events, currentZoom);
  }, [events, currentZoom]);

  // ========================================
  // MARKER CREATION UTILITIES
  // ========================================

  const getOrCreateMarkerHTML = useCallback((cacheKey: string, markerProps: any) => {
    let cachedHTML = markerHTMLCache.current.get(cacheKey);
    if (!cachedHTML) {
      cachedHTML = ReactDOMServer.renderToString(<MapMarker {...markerProps} />);
      markerHTMLCache.current.set(cacheKey, cachedHTML);

      if (markerHTMLCache.current.size > 50) {
        const firstKey = markerHTMLCache.current.keys().next().value;
        if (firstKey) {
          markerHTMLCache.current.delete(firstKey);
        }
      }
    }
    return cachedHTML;
  }, []);

  const createMarkerElement = useCallback((cluster: ReturnType<typeof clusterEvents>[0], activeEventId: string | null) => {
    const div = document.createElement('div');
    div.className = 'custom-marker-icon';

    const isCluster = cluster.events.length > 1;
    const isSingleEvent = cluster.events.length === 1;
    const isSelected = isSingleEvent && cluster.events[0].id === activeEventId;
    const isFavorite = isSingleEvent && localStorage.getItem(`favorite-${cluster.events[0].id}`) === 'true';
    const size = isCluster ? 'lg' : 'md';

    const markerProps = {
      size: size as 'sm' | 'md' | 'lg',
      isSelected,
      isFavorite,
      isCluster,
      count: isCluster ? cluster.events.length : undefined
    };

    const cacheKey = `${size}-${isSelected ? 'selected' : 'normal'}-${isFavorite ? 'fav' : 'normal'}-${isCluster ? cluster.events.length : 'single'}`;
    const markerHTML = getOrCreateMarkerHTML(cacheKey, markerProps);

    div.innerHTML = markerHTML;
    return div;
  }, [getOrCreateMarkerHTML]);

  const createUserLocationElement = (hasNearbyEvents = false) => {
    const div = document.createElement('div');
    div.className = `custom-marker-icon ${hasNearbyEvents ? 'has-nearby-events' : ''}`;
    div.style.pointerEvents = 'none';
    div.innerHTML = `<div class="user-location-marker ${hasNearbyEvents ? 'has-nearby-events' : ''}"><div class="user-location-dot"></div></div>`;
    return div;
  };

  // ========================================
  // EVENT HANDLERS
  // ========================================

  const detectNearbyEvents = useCallback((userLat: number, userLng: number): boolean => {
    return events.some((event: Event) => {
      if (typeof event.latitude !== 'number' || typeof event.longitude !== 'number') {
        return false;
      }
      const distance = calculateDistance(userLat, userLng, event.latitude, event.longitude);
      return distance < 0.1;
    });
  }, [events]);

  const ensureMarkerVisibility = useCallback((lat: number, lng: number): Promise<void> => {
    return new Promise((resolve) => {
      if (!map.current) {
        resolve();
        return;
      }

      const viewportHeight = window.innerHeight;
      const bottomNavHeight = 64;
      const modalHeight = viewportHeight * 0.3;
      const margin = 100;
      const targetY = viewportHeight - (bottomNavHeight + modalHeight + margin);

      const point = map.current.project([lng, lat]);

      if (point.y > targetY) {
        const currentCenter = map.current.getCenter();
        const targetPoint = new maplibregl.Point(point.x, targetY);
        const targetLatLng = map.current.unproject(targetPoint);

        const latDiff = targetLatLng.lat - lat;
        const newCenter: [number, number] = [
          currentCenter.lng,
          currentCenter.lat - latDiff
        ];

        map.current.once('moveend', () => {
          setTimeout(resolve, 100);
        });

        map.current.panTo(newCenter, { duration: 500 });
      } else {
        resolve();
      }
    });
  }, []);

  const checkVisibleEvents = useCallback(() => {
    if (!map.current) return;

    try {
      const bounds = map.current.getBounds();
      const visibleCount = clusters.reduce((count, cluster) => {
        const isVisible = bounds.contains([cluster.center.lng, cluster.center.lat]);
        return count + (isVisible ? cluster.events.length : 0);
      }, 0);
      setVisibleEvents(visibleCount);
    } catch (error) {
      console.error('checkVisibleEvents error:', error);
    }
  }, [clusters]);

  const handleMarkerClick = useCallback((cluster: ReturnType<typeof clusterEvents>[0]) => {
    analytics.trackPinClick(cluster.center, cluster.events);
    cluster.events.forEach(event => {
      analytics.trackEventAppearance(event, 'carte_pin');
    });
    ensureMarkerVisibility(cluster.center.lat, cluster.center.lng);
    onEventClick(cluster.events[0], cluster.events);
  }, [onEventClick, analytics, ensureMarkerVisibility]);

  const handleResetNorth = useCallback(() => {
    console.log('[Compass] 3️⃣ handleResetNorth appelé', {
      hasMap: !!map.current,
      bearingAvant: map.current?.getBearing(),
    });
    if (!map.current) {
      console.warn('[Compass] ❌ map.current est null, abort');
      return;
    }
    map.current.stop(); // Cancel any ongoing gesture/animation
    console.log('[Compass] 4️⃣ stop() effectué, lancement easeTo');
    map.current.easeTo({
      bearing: 0,
      duration: 300
    });
    setTimeout(() => {
      console.log('[Compass] 5️⃣ après 350ms, bearing =', map.current?.getBearing());
    }, 350);
  }, []);

  const handleSearchArea = async () => {
    if (!map.current) return;

    const center = map.current.getCenter();
    setIsSearching(true);

    try {
      const newLocation = {
        latitude: center.lat,
        longitude: center.lng,
        timestamp: Date.now(),
        source: 'manual'
      };

      localStorage.setItem('userLocation', JSON.stringify(newLocation));
      window.dispatchEvent(new StorageEvent('storage', {
        key: 'userLocation',
        newValue: JSON.stringify(newLocation)
      }));

      if (onLocationChange) {
        onLocationChange({
          latitude: center.lat,
          longitude: center.lng,
          shouldCenter: true
        });
      }

      // Mettre à jour le mapStore
      setSearchCenter(center.lat, center.lng);
    } finally {
      setIsSearching(false);
    }
  };

  // ========================================
  // EFFET 1 : Initialisation de la carte
  // ========================================

  useEffect(() => {
    if (!mapContainer.current || !shouldShowMap || !finalLocation || !webglAvailable) return;

    // Éviter la recréation si la carte existe déjà
    if (map.current) {
      return;
    }

    console.log('🗺️ [MapLibreView] Initialisation de la carte');

    map.current = new maplibregl.Map({
      container: mapContainer.current,
      style: getMapStyle(CURRENT_MAP_STYLE) as maplibregl.StyleSpecification | string,
      center: finalLocation,
      zoom: MAP_CONFIG.DEFAULT_ZOOM,
      maxZoom: MAP_CONFIG.MAX_ZOOM,
      fadeDuration: 0
    });

    // Gestionnaire de chargement
    map.current.on('load', () => {
      if (map.current && finalLocation) {
        requestAnimationFrame(() => {
          if (map.current) {
            map.current.resize();
            map.current.jumpTo({
              center: finalLocation,
              zoom: MAP_CONFIG.DEFAULT_ZOOM
            });
            // Initialiser le centre de recherche
            setSearchCenter(finalLocation[1], finalLocation[0]);
          }
        });
      }
      setReady(true);
    });

    // Gestionnaire de déplacement (throttled)
    let moveTimeout: NodeJS.Timeout;
    let lastMoveWasUserInitiated = false;
    map.current.on('moveend', (e) => {
      if (!map.current) return;

      // MapLibre attache `originalEvent` uniquement quand le déplacement
      // vient d'un geste utilisateur (drag, pinch). Les flyTo/jumpTo/panTo
      // programmatiques le laissent undefined → pas de bouton "Search this area".
      lastMoveWasUserInitiated = !!(e as { originalEvent?: Event }).originalEvent;

      clearTimeout(moveTimeout);
      moveTimeout = setTimeout(() => {
        if (!map.current) return;
        const center = map.current.getCenter();
        setCenter(center.lat, center.lng);
        checkVisibleEvents();
        if (lastMoveWasUserInitiated) {
          updateSearchButtonVisibility(center.lat, center.lng);
        }
      }, 100);
    });

    // Gestionnaire de zoom (throttled)
    let zoomTimeout: NodeJS.Timeout;
    map.current.on('zoomend', () => {
      if (!map.current) return;

      clearTimeout(zoomTimeout);
      zoomTimeout = setTimeout(() => {
        const newZoom = map.current!.getZoom();
        setZoom(newZoom);
        setCurrentZoom(newZoom);
        checkVisibleEvents();
      }, 50);
    });

    // Gestionnaire de rotation
    map.current.on('rotate', () => {
      if (!map.current) return;
      setBearing(map.current.getBearing());
    });

    // Gestionnaire de clic sur la carte
    map.current.on('click', () => {
      if (onMapClick) {
        onMapClick();
      }
    });

    // Nettoyage
    return () => {
      if (map.current) {
        clearTimeout(moveTimeout);
        clearTimeout(zoomTimeout);
        map.current.remove();
        map.current = null;
        setReady(false);
      }
    };
  }, [shouldShowMap, webglAvailable]); // Dépendances minimales

  // ========================================
  // EFFET 2 : UN SEUL effet pour le centrage (flyTo)
  // ========================================

  useEffect(() => {
    if (!map.current || !mapReady || !pendingFlyTo) return;

    console.log('🗺️ [MapLibreView] Exécution flyTo:', pendingFlyTo);

    map.current.flyTo({
      center: [pendingFlyTo.lng, pendingFlyTo.lat],
      zoom: MAP_CONFIG.DEFAULT_ZOOM,
      duration: MAP_CONFIG.FLY_DURATION
    });

    // Nettoyer le pendingFlyTo après le démarrage de l'animation
    clearPendingFlyTo();

    // Marquer la fin de l'animation après la durée
    setTimeout(() => {
      setAnimating(false);
    }, MAP_CONFIG.FLY_DURATION + 200);

  }, [mapReady, pendingFlyTo, clearPendingFlyTo, setAnimating]);

  // ========================================
  // EFFET 3 : UN SEUL effet pour jumpTo
  // ========================================

  useEffect(() => {
    if (!map.current || !mapReady || !pendingJumpTo) return;

    console.log('🗺️ [MapLibreView] Exécution jumpTo:', pendingJumpTo);

    map.current.jumpTo({
      center: [pendingJumpTo.lng, pendingJumpTo.lat],
      zoom: MAP_CONFIG.DEFAULT_ZOOM
    });

    clearPendingJumpTo();
  }, [mapReady, pendingJumpTo, clearPendingJumpTo]);

  // ========================================
  // EFFET 4 : Réagir aux changements de initialLocation
  // ========================================

  const prevInitialLocationRef = useRef<{ latitude: number; longitude: number } | null>(null);

  useEffect(() => {
    if (!mapReady || !initialLocation) return;

    const prev = prevInitialLocationRef.current;

    // Vérifier si c'est un changement significatif
    const hasSignificantChange = prev && (
      Math.abs(prev.latitude - initialLocation.latitude) > 0.01 ||
      Math.abs(prev.longitude - initialLocation.longitude) > 0.01
    );

    // Si c'est un nouveau lieu ou un changement significatif
    if (!prev || hasSignificantChange) {
      console.log('🗺️ [MapLibreView] Changement de initialLocation détecté, flyTo');
      // flyTo() sync `lastSearchCenter` vers la destination (cf. mapStore.ts)
      flyTo(initialLocation.latitude, initialLocation.longitude);
    }

    prevInitialLocationRef.current = initialLocation;
  }, [initialLocation, mapReady, flyTo]);

  // ========================================
  // EFFET 5 : Mettre à jour les marqueurs
  // ========================================

  useEffect(() => {
    if (!map.current || !mapReady) return;

    // Supprimer les anciens marqueurs
    markers.current.forEach(marker => marker.remove());
    markers.current = [];

    // Ajouter les nouveaux marqueurs avec animation
    clusters.forEach((cluster, index) => {
      const el = createMarkerElement(cluster, null);

      const markerElement = el.querySelector('.map-marker') as HTMLElement;
      if (markerElement) {
        markerElement.classList.add('marker-drop-in');
        markerElement.style.animationDelay = `${index * 40}ms`;
      }

      const marker = new maplibregl.Marker({
        element: el,
        anchor: cluster.events.length > 1 ? 'center' : 'bottom'
      })
        .setLngLat([cluster.center.lng, cluster.center.lat])
        .addTo(map.current!);

      el.addEventListener('click', (e) => {
        e.stopPropagation();
        handleMarkerClick(cluster);
      });

      markers.current.push(marker);
    });
  }, [clusters, mapReady, handleMarkerClick, createMarkerElement]);

  // ========================================
  // EFFET 5b : Skeleton markers pendant le chargement
  // ========================================
  // Skeletons visibles uniquement pendant le chargement initial :
  // - mapReady = true (map affichée)
  // - markers.current.length = 0 (pas encore de vrais marqueurs)
  // - isLoading = true (données en cours de fetch)
  // Dès que isLoading passe à false (chargement terminé), les skeletons disparaissent
  // que des events soient trouvés ou non.
  useEffect(() => {
    // Nettoyer les anciens skeleton markers
    skeletonMarkers.current.forEach(m => m.remove());
    skeletonMarkers.current = [];

    // Montrer les skeletons UNIQUEMENT pendant le chargement
    if (!map.current || !mapReady || markers.current.length > 0 || !isLoading) return;

    const center = map.current.getCenter();
    // 5 positions proches du centre (offset ~0.001-0.004 degrés ≈ 100-400m)
    const offsets = [
      { lng: -0.002 + Math.random() * 0.001, lat: 0.002 + Math.random() * 0.001 },
      { lng: 0.003 + Math.random() * 0.001, lat: -0.001 + Math.random() * 0.001 },
      { lng: -0.001 + Math.random() * 0.001, lat: -0.003 + Math.random() * 0.001 },
      { lng: 0.001 + Math.random() * 0.001, lat: 0.001 + Math.random() * 0.001 },
      { lng: -0.003 + Math.random() * 0.001, lat: 0.000 + Math.random() * 0.001 },
    ];

    offsets.forEach((offset, index) => {
      const el = document.createElement('div');
      el.className = 'custom-marker-icon';
      el.innerHTML = '<div class="map-marker map-marker--skeleton"></div>';

      const skeletonEl = el.querySelector('.map-marker') as HTMLElement;
      if (skeletonEl) {
        skeletonEl.style.animationDelay = `${index * 0.15}s`;
      }

      const marker = new maplibregl.Marker({ element: el, anchor: 'bottom' })
        .setLngLat([center.lng + offset.lng, center.lat + offset.lat])
        .addTo(map.current!);

      skeletonMarkers.current.push(marker);
    });

    return () => {
      skeletonMarkers.current.forEach(m => m.remove());
      skeletonMarkers.current = [];
    };
  }, [mapReady, clusters.length, isLoading]);

  // ========================================
  // EFFET 6 : Marqueur de position utilisateur
  // ========================================

  useEffect(() => {
    if (!map.current || !mapReady || !location) return;

    if (userMarker.current) {
      userMarker.current.remove();
      userMarker.current = null;
    }

    const hasNearbyEvents = detectNearbyEvents(location.latitude, location.longitude);
    const offset = hasNearbyEvents ? [0.0002, 0.0002] : [0, 0];

    const el = createUserLocationElement(hasNearbyEvents);
    userMarker.current = new maplibregl.Marker({
      element: el,
      anchor: 'center'
    })
      .setLngLat([location.longitude + offset[0], location.latitude + offset[1]])
      .addTo(map.current);
  }, [location, mapReady, detectNearbyEvents]);

  // ========================================
  // EFFET 7 : ResizeObserver simplifié
  // ========================================

  useEffect(() => {
    if (!mapReady || !map.current || !mapContainer.current) return;

    let lastWidth = 0;
    let lastHeight = 0;
    let resizeTimeout: NodeJS.Timeout;

    const resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      const { width, height } = entry.contentRect;

      // Seulement si la taille change significativement
      if (Math.abs(width - lastWidth) > 10 || Math.abs(height - lastHeight) > 10) {
        const wasZeroSize = lastWidth === 0 && lastHeight === 0;
        const isNowVisible = width > 0 && height > 0;
        lastWidth = width;
        lastHeight = height;

        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          if (map.current) {
            map.current.resize();

            // Recentrer seulement au premier affichage
            if (wasZeroSize && isNowVisible && finalLocation) {
              map.current.jumpTo({
                center: finalLocation,
                zoom: MAP_CONFIG.DEFAULT_ZOOM
              });
            }
          }
        }, 100);
      }
    });

    const parentContainer = document.querySelector('.home__map-view');
    if (parentContainer) {
      resizeObserver.observe(parentContainer);
    }

    return () => {
      clearTimeout(resizeTimeout);
      resizeObserver.disconnect();
    };
  }, [mapReady, finalLocation]);

  // ========================================
  // EFFET 8 : Événement mapReady (pour animations externes)
  // ========================================

  useEffect(() => {
    if (clusters.length > 0 && !isSearching && shouldShowMap) {
      const timer = setTimeout(() => {
        window.dispatchEvent(new CustomEvent('mapReady'));
      }, 100);
      return () => clearTimeout(timer);
    } else if (events.length >= 0 && !isSearching && shouldShowMap) {
      window.dispatchEvent(new CustomEvent('mapReady'));
    }
  }, [clusters, events, isSearching, shouldShowMap]);

  // ========================================
  // RENDU
  // ========================================

  if (!webglAvailable) {
    return (
      <div className="map-container">
        <MapFallback onSwitchToList={onSwitchToList} />
      </div>
    );
  }

  if (!shouldShowMap) {
    return (
      <div className="map-container">
        <div className="map-loading-indicator">
          <Icon name="Loader" size={20} /> locating in progress...
        </div>
      </div>
    );
  }

  return (
    <div className="map-container">
      {isSearching && (
        <div className="map-loading-indicator">
          <Icon name="Loader" size={20} /> Chargement...
        </div>
      )}

      <div ref={mapContainer} style={{ height: '100%', width: '100%' }} />

      <CompassButton bearing={bearing} onClick={handleResetNorth} />

      {!isSearching &&
        !isLoading &&
        !shouldShowSearchButton &&
        visibleEvents === 0 &&
        clusters.length === 0 &&
        mapReady && (
          <EmptyStateMessage />
        )}

      {shouldShowSearchButton && !isSearching && mapReady && (
        <SearchAreaButton
          onClick={handleSearchArea}
          isLoading={isSearching}
        />
      )}
    </div>
  );
});

export default MapLibreView;
